export interface WeekKey {
  iso_year: number
  iso_week: number
}

export function weekId(w: WeekKey): string {
  return `${w.iso_year}-${w.iso_week}`
}

export function isoParts(iso: string): WeekKey {
  const parts = iso.split('-').map(Number)
  const y = parts[0] ?? 1970
  const m = parts[1] ?? 1
  const d = parts[2] ?? 1
  const date = new Date(Date.UTC(y, m - 1, d))
  const day = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() + 4 - day)
  const isoYear = date.getUTCFullYear()
  const start = new Date(Date.UTC(isoYear, 0, 1))
  const week = Math.ceil((((date.getTime() - start.getTime()) / 86400000) + 1) / 7)
  return { iso_year: isoYear, iso_week: week }
}

export function sentenceTime(hhmm: string): string {
  const [h, m] = hhmm.split(':')
  return `${Number(h)}:${m ?? '00'}`
}

export function joinTimes(times: string[]): string {
  const t = times.map(sentenceTime)
  if (t.length <= 1) return t[0] ?? ''
  if (t.length === 2) return `${t[0]} und ${t[1]}`
  return `${t.slice(0, -1).join(', ')} und ${t[t.length - 1]}`
}

export function releaseSentence(start: string, lastName: string): string {
  return `${sentenceTime(start)} entfernen — ${lastName} unterrichtet selbst.`
}

export function abortSentence(liveCount: number, losses: { name: string; times: string[] }[]): string {
  let text = `Suche abbrechen — alle ${liveCount} zurück`
  if (losses.length) {
    const parts = losses.map(l => `${l.name} verliert ${joinTimes(l.times)}`)
    const joined = parts.length === 1
      ? parts[0]
      : `${parts.slice(0, -1).join(', ')} und ${parts[parts.length - 1]}`
    text += `, ${joined}`
  }
  return `${text}.`
}

export function caseWeekKeys(lessons: Array<{ date: string | null; status: string | null }>): WeekKey[] {
  const seen = new Set<string>()
  const keys: WeekKey[] = []
  for (const lesson of lessons) {
    if (!lesson.date || lesson.status === 'cancelled' || lesson.status === 'director_removed') continue
    const key = isoParts(lesson.date.slice(0, 10))
    const id = weekId(key)
    if (seen.has(id)) continue
    seen.add(id)
    keys.push(key)
  }
  return keys
}

export function lessonRange(
  lesson: {
    time_slot_name?: string | null
    start_time?: string | null
    end_time?: string | null
    effective_start_time?: string | null
    effective_end_time?: string | null
  },
  sep = '–',
): string | null {
  const start = lesson.effective_start_time || lesson.start_time
  const end = lesson.effective_end_time || lesson.end_time
  const clipped = Boolean(
    (lesson.effective_start_time && lesson.effective_start_time !== lesson.start_time)
    || (lesson.effective_end_time && lesson.effective_end_time !== lesson.end_time),
  )
  if (clipped && start && end) return `${start}${sep}${end}`
  return lesson.time_slot_name
    || (lesson.start_time && lesson.end_time ? `${lesson.start_time}${sep}${lesson.end_time}` : null)
}

export function sameWeek(a: WeekKey | null, b: WeekKey | null): boolean {
  if (!a || !b) return false
  return a.iso_year === b.iso_year && a.iso_week === b.iso_week
}

const COMMITTED = ['accepted', 'assigned', 'filled', 'booked']
const GONE_PARENT = new Set(['cancelled', 'declined', 'failed'])

export function parentIsGone(status: string | null | undefined): boolean {
  return GONE_PARENT.has(status ?? '')
}

export function caseSentenceAbortVisible(
  detail: {
    conditional_approval: boolean
    status: string
    lessons: Array<{ status: string | null; assigned_teacher_id: string | null }>
  },
  readOnly = false,
): boolean {
  if (readOnly || !detail.conditional_approval) return false
  if (detail.status === 'booked' || parentIsGone(detail.status)) return false
  const live = detail.lessons.some(l => l.status !== 'cancelled' && l.status !== 'director_removed')
  const losses = detail.lessons.some(l => l.assigned_teacher_id && COMMITTED.includes(l.status ?? ''))
  return live || losses
}
